import { useEffect, useState, useRef } from "react";
import { AppLayout } from "@/components/AppLayout";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, MapPin } from "@phosphor-icons/react";
import { MonthYearPicker, DatePicker } from "@/components/inputs";
import { useEntryGuard } from "@/lib/useEntryGuard";
import { AttendanceStatusPill } from "@/components/AttendanceStatusPill";
import { AttendanceMonthlyRecap } from "@/components/AttendanceMonthlyRecap";

function formatDuration(mins) {
  if (mins == null || mins === "" || isNaN(Number(mins))) return "—";
  const n = Number(mins);
  if (n <= 0) return "0 menit";
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (h > 0 && m > 0) return `${h} jam ${m} menit`;
  if (h > 0) return `${h} jam`;
  return `${m} menit`;
}

async function fetchAddress(lat, lng) {
  if (lat == null || lng == null) return "";
  const gkey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (gkey) {
    try {
      const r = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${gkey}&language=id`);
      if (r.ok) {
        const j = await r.json();
        if (j.results && j.results[0]) return j.results[0].formatted_address;
      }
    } catch {}
  }
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=id`, { headers: { "Accept": "application/json" } });
    if (r.ok) {
      const j = await r.json();
      if (j.display_name) return j.display_name;
    }
  } catch {}
  return "";
}

function CaptureDialog({ open, onOpenChange, title, onConfirm, gps, setGps }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [captured, setCaptured] = useState(null);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [loadingGps, setLoadingGps] = useState(false);

  useEffect(() => {
    if (!open) {
      if (stream) stream.getTracks().forEach((t) => t.stop());
      setStream(null);
      setCaptured(null);
      setFile(null);
      return;
    }
    setGps(null);
    // GPS otomatis terekam tanpa perlu pencet tombol + alamat lengkap
    if (navigator.geolocation) {
      setLoadingGps(true);
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const base = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
          setGps(base);
          setLoadingGps(false);
          try {
            const addr = await fetchAddress(base.lat, base.lng);
            if (addr) setGps({ ...base, address: addr });
          } catch {}
        },
        () => setLoadingGps(false),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    }
    let s;
    (async () => {
      try {
        s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        setStream(s);
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch (e) {
        toast.error("Kamera tidak tersedia, gunakan upload file");
      }
    })();
    return () => { if (s) s.getTracks().forEach((t) => t.stop()); };
  }, [open]);

  useEffect(() => {
    if (videoRef.current && stream) videoRef.current.srcObject = stream;
  }, [stream]);

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const f = new File([blob], "selfie.jpg", { type: "image/jpeg" });
      setFile(f);
      setCaptured(canvas.toDataURL("image/jpeg", 0.8));
    }, "image/jpeg", 0.8);
  };

  const onFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const url = URL.createObjectURL(f);
    setCaptured(url);
  };

  const acquireGps = () => {
    setLoadingGps(true);
    if (!navigator.geolocation) {
      toast.error("GPS tidak didukung perangkat");
      setLoadingGps(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const base = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
        setGps(base);
        setLoadingGps(false);
        toast.success("Lokasi didapat");
        try {
          const addr = await fetchAddress(base.lat, base.lng);
          if (addr) setGps({ ...base, address: addr });
        } catch {}
      },
      (err) => {
        toast.error(err.message || "Gagal mendapatkan lokasi");
        setLoadingGps(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const handleConfirm = () => {
    if (!file) { toast.error("Ambil atau upload foto selfie dulu"); return; }
    onConfirm(file, gps);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[360px] md:max-w-[380px] max-h-[85vh] overflow-y-auto p-4 md:p-5">
        <DialogHeader><DialogTitle className="text-base">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg overflow-hidden bg-black aspect-[4/3] max-h-[180px] md:max-h-[200px] flex items-center justify-center">
            {captured ? (
              <img src={captured} alt="preview" className="w-full h-full object-contain bg-black" />
            ) : (
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            )}
            <canvas ref={canvasRef} className="hidden" />
          </div>
          <div className="flex gap-2">
            {!captured ? (
              <Button onClick={capture} className="flex-1 gap-2 bg-[#00364e] text-white hover:bg-[#1b4d68]" data-testid="capture-button"><Camera size={18} /> Ambil Foto</Button>
            ) : (
              <Button variant="outline" onClick={() => { setCaptured(null); setFile(null); }} className="flex-1">Ulangi</Button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} data-testid="selfie-file-input" />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()}>Upload</Button>
          </div>
          <div className="space-y-2">
            <Label>Lokasi GPS (terekam otomatis)</Label>
            <div className="flex gap-2 items-center">
              <span className="text-xs text-muted-foreground" data-testid="gps-status">
                {loadingGps ? "Mencari lokasi…" : gps ? `${gps.lat?.toFixed(5)}, ${gps.lng?.toFixed(5)} ±${Math.round(gps.accuracy || 0)}m` : "Menunggu izin lokasi…"}
              </span>
              <Button variant="ghost" size="sm" onClick={acquireGps} disabled={loadingGps} className="gap-1 ml-auto" data-testid="gps-button"><MapPin size={14} /> {gps ? "Perbarui" : "Coba lagi"}</Button>
            </div>
            {gps?.address ? (
              <p className="text-xs text-muted-foreground bg-muted rounded-md p-2" data-testid="gps-address">{gps.address}</p>
            ) : gps && !loadingGps ? (
              <p className="text-xs text-muted-foreground">Mengambil alamat…</p>
            ) : null}
            {gps?.address && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${gps.lat},${gps.lng}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline" data-testid="gps-maps-link">Lihat di Google Maps</a>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={handleConfirm} disabled={!file} className="bg-[#00364e] text-white hover:bg-[#1b4d68]" data-testid="confirm-selfie">Konfirmasi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AttendancePage() {
  const now = new Date();
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [leaves, setLeaves] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [captureMode, setCaptureMode] = useState(null);
  const [gps, setGps] = useState(null);
  const [busy, setBusy] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [lateOpen, setLateOpen] = useState(false);
  const [corrOpen, setCorrOpen] = useState(false);
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const hadirCount = history.filter((h) => h.status === "hadir").length;
  const terlambatCount = history.filter((h) => h.status === "terlambat").length;
  const attendanceRate = history.length > 0 ? Math.round((hadirCount / history.length) * 100) : 0;
  const pendingCount =
    leaves.filter((l) => l.status !== "approved" && l.status !== "rejected").length +
    corrections.filter((c) => c.status !== "approved" && c.status !== "rejected").length;
  const clockText = clock.toLocaleTimeString("id-ID", { hour12: false });
  const clockDate = clock.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const hasGps = !!(today?.check_in?.lat || today?.check_out?.lat);
  const [photoIdx, setPhotoIdx] = useState(0);
  const presensiPhotos = [
    ...(today?.check_in?.photo_path ? [{ key: "in", label: `Check-in ${today.check_in.time} WIB`, src: fileUrl(today.check_in.photo_path), lat: today.check_in.lat, lng: today.check_in.lng }] : []),
    ...(today?.check_out?.photo_path ? [{ key: "out", label: `Check-out ${today.check_out.time} WIB`, src: fileUrl(today.check_out.photo_path), lat: today.check_out.lat, lng: today.check_out.lng }] : []),
  ];
  const activePhoto = presensiPhotos.length > 0 ? presensiPhotos[Math.min(photoIdx, presensiPhotos.length - 1)] : null;
  const statusTitle = today?.check_out ? "Sudah Check-Out" : today?.check_in ? "Sudah Check-In" : "Belum Check-In";
  const [tab, setTab] = useState("absen");
  const attendanceTabs = [
    { id: "absen", label: "Absen" },
    { id: "sakitcuti", label: "Ajukan Sakit/Cuti" },
    { id: "terlambat", label: "Ajukan Terlambat/Pulang Cepat" },
    { id: "koreksi", label: "Ajukan Koreksi" },
  ];

  const loadToday = () => api.get("/attendance/me/today").then((r) => setToday(r.data)).catch(() => {});
  const loadHistory = () => api.get(`/attendance/me?month=${month}&year=${year}`).then((r) => setHistory(r.data)).catch(() => {});
  const loadLeaves = () => api.get("/attendance/leave-requests/me").then((r) => setLeaves(r.data)).catch(() => {});
  const loadCorr = () => api.get("/attendance/corrections/me").then((r) => setCorrections(r.data)).catch(() => {});

  useEffect(() => { loadToday(); }, []);
  useEffect(() => { loadHistory(); }, [month, year]);
  useEffect(() => { loadLeaves(); loadCorr(); }, []);

  const uploadSelfie = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return res.data.path;
  };

  const doCheckIn = async (file, pos) => {
    setBusy(true);
    try {
      const path = await uploadSelfie(file);
      await api.post("/attendance/check-in", { photo_path: path, lat: pos?.lat || null, lng: pos?.lng || null, accuracy: pos?.accuracy || null, address: pos?.address || null });
      toast.success("Check-in berhasil");
      setCaptureMode(null);
      loadToday(); loadHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  const doCheckOut = async (file, pos) => {
    setBusy(true);
    try {
      const path = await uploadSelfie(file);
      await api.post("/attendance/check-out", { photo_path: path, lat: pos?.lat || null, lng: pos?.lng || null, accuracy: pos?.accuracy || null, address: pos?.address || null });
      toast.success("Check-out berhasil");
      setCaptureMode(null);
      loadToday(); loadHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const sakitCuti = leaves.filter((l) => ["sakit", "cuti"].includes(l.type));
  const sakitCutiWithLegacy = leaves.filter((l) => ["sakit", "cuti", "izin"].includes(l.type));
  const displaySakitCuti = sakitCuti.length > 0 ? sakitCuti : sakitCutiWithLegacy;
  const terlambatLeaves = leaves.filter((l) => ["terlambat", "pulang_cepat"].includes(l.type));
  const inSelectedPeriod = (iso) => {
    const m = String(iso || "").match(/^(\d{4})-(\d{2})/);
    if (!m) return true;
    return Number(m[2]) === Number(month) && Number(m[1]) === Number(year);
  };
  const filteredSakitCuti = displaySakitCuti.filter((l) => inSelectedPeriod(l.start_date));
  const filteredTerlambat = terlambatLeaves.filter((l) => inSelectedPeriod(l.start_date));
  const filteredCorrections = corrections.filter((c) => inSelectedPeriod(c.date));

  const typeLabel = (t) => {
    const m = { sakit: "Sakit", cuti: "Cuti", izin: "Izin", terlambat: "Datang Terlambat", pulang_cepat: "Pulang Cepat" };
    return m[t] || t;
  };
  return (
    <AppLayout title="Absensi">
      <div className="max-w-6xl space-y-6 animate-fade-up">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <p className="text-[14px] text-[#64748B]">Portal Karyawan <span className="mx-1">›</span> <span className="text-[#006398] font-medium">Absensi &amp; Kehadiran</span></p>
            <h1 className="skn-h1 text-[28px] md:text-[36px] leading-tight mt-1">Absensi &amp; Presensi Harian</h1>
            <p className="text-[14px] text-[#64748B] mt-0.5">Pantau ketepatan waktu dan riwayat kehadiran Anda.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-start lg:self-auto">
            <MonthYearPicker month={month} year={year} onChange={(m,y)=>{setMonth(m); setYear(y);}} years={[year-2,year-1,year,year+1]} monthTestId="attendance-month" yearTestId="attendance-year" />
            <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#eff4ff] text-[14px] text-[#41484d] shadow-sm">
              <span className={`w-2 h-2 rounded-full ${hasGps ? "bg-[#059669] animate-pulse" : "bg-[#64748B]"}`}></span>
              <span className="font-medium">{hasGps ? "GPS Tervalidasi" : "Belum Check-In"}</span>
            </span>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Navigasi absensi">
          {attendanceTabs.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className={`whitespace-nowrap px-4 py-2 rounded-lg text-[14px] font-semibold transition-colors ${tab === t.id ? "bg-[#00364e] text-white shadow-sm" : "bg-white text-[#41484d] hover:bg-[#eff4ff]"}`}
              data-testid={`tab-${t.id}`}>{t.label}</button>
          ))}
        </div>

        {tab === "absen" && (<>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 flex flex-col p-6 rounded-xl bg-white shadow-sm relative overflow-hidden">
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#00364e] text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                  <span className="material-symbols-outlined text-[32px]">fingerprint</span>
                </div>
                <div className="flex flex-col">
                  <span className="skn-section-label">Status Presensi Hari Ini</span>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-[24px] font-bold text-[#0b1c30]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{statusTitle}</span>
                    {today?.status && <AttendanceStatusPill status={today.status} />}
                  </div>
                  {(today?.check_in || today?.check_out) && (
                    <div className="flex items-center gap-3 mt-1 text-[14px] text-[#64748B] flex-wrap">
                      {today?.check_in && <span>Log Masuk: <span className="font-medium text-[#059669]">{today.check_in.time} WIB</span></span>}
                      {today?.check_out && <span>Log Pulang: <span className="font-medium text-[#059669]">{today.check_out.time} WIB</span></span>}
                      {today?.duration_minutes != null && <Badge variant="outline">{formatDuration(today.duration_minutes)} kerja</Badge>}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end text-right bg-[#eff4ff] px-4 py-2 rounded-xl">
                <div className="text-[28px] md:text-[36px] text-[#00364e] font-extrabold tracking-tight tabular-nums" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{clockText}</div>
                <div className="text-[14px] text-[#64748B] font-medium">{clockDate} (WIB)</div>
              </div>
            </div>
            <div className="relative z-10 pt-4 flex items-center gap-3">
              <Button disabled={busy || !!today?.check_in} onClick={() => setCaptureMode("in")} className="flex-1 min-w-0 h-10 sm:h-12 px-1.5 sm:px-4 text-[11px] sm:text-[15px] gap-1 sm:gap-2 whitespace-nowrap bg-[#00364e] hover:bg-[#1b4d68] text-white" data-testid="checkin-button">
                <span className="material-symbols-outlined text-[16px] sm:text-[20px] flex-shrink-0">photo_camera</span>
                <span className="sm:hidden">{today?.check_in ? `Check-In (${today.check_in.time})` : "Check-In"}</span>
                <span className="hidden sm:inline">{today?.check_in ? `Check-In (${today.check_in.time})` : "Check-In Sekarang"}</span>
              </Button>
              <Button disabled={busy || !today?.check_in || !!today?.check_out} onClick={() => setCaptureMode("out")} className="flex-1 min-w-0 h-10 sm:h-12 px-1.5 sm:px-4 text-[11px] sm:text-[15px] gap-1 sm:gap-2 whitespace-nowrap bg-[#00364e] hover:bg-[#1b4d68] text-white" data-testid="checkout-button">
                <span className="material-symbols-outlined text-[16px] sm:text-[20px] flex-shrink-0">logout</span>
                <span className="sm:hidden">{today?.check_out ? `Check-Out (${today.check_out.time})` : "Check-Out"}</span>
                <span className="hidden sm:inline">{today?.check_out ? `Check-Out (${today.check_out.time})` : "Check-Out Sekarang"}</span>
              </Button>
            </div>
            <div className="relative z-10 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <span className="material-symbols-outlined text-[#0284C7] text-[20px] mt-0.5 flex-shrink-0">my_location</span>
                <div className="min-w-0">
                  <p className="text-[14px] font-bold text-[#0b1c30]">
                    Lokasi Check-In
                    {today?.check_in?.lat != null && <span className="material-symbols-outlined text-[#059669] text-[16px] align-middle ml-1">verified</span>}
                  </p>
                  <p className="text-[14px] text-[#64748B] break-words" data-testid="today-checkin-address">
                    {today?.check_in?.address || (today?.check_in?.lat ? `${today.check_in.lat.toFixed(5)}, ${today.check_in.lng.toFixed(5)}` : "Lakukan check-in untuk merekam lokasi")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2 min-w-0">
                <span className="material-symbols-outlined text-[#0284C7] text-[20px] mt-0.5 flex-shrink-0">my_location</span>
                <div className="min-w-0">
                  <p className="text-[14px] font-bold text-[#0b1c30]">
                    Lokasi Check-Out
                    {today?.check_out?.lat != null && <span className="material-symbols-outlined text-[#059669] text-[16px] align-middle ml-1">verified</span>}
                  </p>
                  <p className="text-[14px] text-[#64748B] break-words" data-testid="today-checkout-address">
                    {today?.check_out?.address || (today?.check_out?.lat ? `${today.check_out.lat.toFixed(5)}, ${today.check_out.lng.toFixed(5)}` : "Belum check-out")}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-4 flex flex-col">
            <div className="rounded-xl bg-white p-6 shadow-sm flex flex-col">
            <div className="flex items-center justify-between pb-2">
              <span className="text-[16px] font-bold text-[#0b1c30]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Foto Presensi</span>
              <span className={`px-2 py-0.5 rounded-full text-[12px] font-semibold ${hasGps ? "bg-[#eff4ff] text-[#64748B]" : "bg-[#eff4ff] text-[#64748B]"}`}>{hasGps ? "Tervalidasi" : "Menunggu"}</span>
            </div>
            <div className="relative w-full h-60 rounded-xl overflow-hidden bg-[#eff4ff] flex items-center justify-center my-2">
              {activePhoto ? (
                <img key={activePhoto.key} src={activePhoto.src} alt={`Foto ${activePhoto.key === "in" ? "check-in" : "check-out"} hari ini`} className="w-full h-full object-cover" data-testid={activePhoto.key === "in" ? "today-checkin-photo" : "today-checkout-photo"} />
              ) : (
                <span className="material-symbols-outlined text-[48px] text-[#9fccec]">photo_camera</span>
              )}
              {presensiPhotos.length > 1 && (
                <>
                  <button type="button" aria-label="Foto sebelumnya" onClick={() => setPhotoIdx((i) => (i - 1 + presensiPhotos.length) % presensiPhotos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70" data-testid="photo-prev">
                    <span className="material-symbols-outlined text-[20px]">chevron_left</span>
                  </button>
                  <button type="button" aria-label="Foto berikutnya" onClick={() => setPhotoIdx((i) => (i + 1) % presensiPhotos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70" data-testid="photo-next">
                    <span className="material-symbols-outlined text-[20px]">chevron_right</span>
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center justify-between pt-1 text-[14px] text-[#64748B]">
              <span>{activePhoto ? activePhoto.label : "Belum ada foto hari ini"}</span>
              {activePhoto?.lat != null && <a href={`https://www.google.com/maps/search/?api=1&query=${activePhoto.lat},${activePhoto.lng}`} target="_blank" rel="noreferrer" className="text-[#006398] font-semibold hover:underline">Lihat Peta</a>}
            </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl bg-white shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between text-[#64748B]">
              <span className="text-[14px] font-medium">Tingkat Kehadiran</span>
              <div className="w-8 h-8 rounded-lg bg-[#eff4ff] flex items-center justify-center text-[#00364e]"><span className="material-symbols-outlined text-[18px]">pie_chart</span></div>
            </div>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="text-[28px] text-[#00364e] font-bold" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{attendanceRate}%</span>
            </div>
            <div className="w-full bg-[#eff4ff] h-1.5 rounded-full mt-3 overflow-hidden"><div className="bg-[#00364e] h-full rounded-full" style={{ width: `${attendanceRate}%` }}></div></div>
            <span className="text-[11px] text-[#64748B] mt-2">{hadirCount} dari {history.length} hari tercatat</span>
          </div>
          <div className="p-4 rounded-xl bg-white shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between text-[#64748B]">
              <span className="text-[14px] font-medium">Hadir Tepat Waktu</span>
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-[#059669]"><span className="material-symbols-outlined text-[18px]">verified</span></div>
            </div>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="text-[28px] text-[#0b1c30] font-bold" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{hadirCount}</span>
              <span className="text-[14px] text-[#64748B] font-medium">Hari</span>
            </div>
            <div className="w-full bg-[#eff4ff] h-1.5 rounded-full mt-3 overflow-hidden"><div className="bg-[#059669] h-full rounded-full" style={{ width: `${attendanceRate}%` }}></div></div>
            <span className="text-[11px] text-[#64748B] mt-2">Periode berjalan</span>
          </div>
          <div className="p-4 rounded-xl bg-white shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between text-[#64748B]">
              <span className="text-[14px] font-medium">Terlambat Datang</span>
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-[#D97706]"><span className="material-symbols-outlined text-[18px]">alarm_off</span></div>
            </div>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="text-[28px] text-[#0b1c30] font-bold" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{terlambatCount}</span>
              <span className="text-[14px] text-[#64748B] font-medium">Hari</span>
            </div>
            <div className="w-full bg-[#eff4ff] h-1.5 rounded-full mt-3 overflow-hidden"><div className="bg-[#D97706] h-full rounded-full" style={{ width: `${history.length ? Math.round((terlambatCount / history.length) * 100) : 0}%` }}></div></div>
            <span className="text-[11px] text-[#64748B] mt-2">Periode berjalan</span>
          </div>
          <div className="p-4 rounded-xl bg-white shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between text-[#64748B]">
              <span className="text-[14px] font-medium">Pengajuan Aktif</span>
              <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center text-[#0284C7]"><span className="material-symbols-outlined text-[18px]">event_available</span></div>
            </div>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="text-[28px] text-[#0b1c30] font-bold" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{pendingCount}</span>
              <span className="text-[14px] text-[#64748B] font-medium">Pengajuan</span>
            </div>
            <div className="w-full bg-[#eff4ff] h-1.5 rounded-full mt-3 overflow-hidden"><div className="bg-[#0284C7] h-full rounded-full" style={{ width: `${pendingCount > 0 ? 100 : 0}%` }}></div></div>
            <span className="text-[11px] text-[#64748B] mt-2">Izin, cuti &amp; koreksi menunggu</span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 items-start">
          <div className="flex flex-col rounded-xl bg-white shadow-sm overflow-hidden">
            <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex flex-col">
                <h2 className="text-[20px] font-bold text-[#0b1c30]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Riwayat Log Kehadiran</h2>
                <span className="text-[14px] text-[#64748B]">Menampilkan {history.length} catatan presensi</span>
              </div>
              <span className="text-[14px] text-[#64748B]">Periode {String(month).padStart(2, "0")}/{year}</span>
            </div>
            <AttendanceMonthlyRecap history={history} month={month} year={year} />
            <div className="w-full overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-[#eff4ff]"><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Tanggal</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Jam Masuk</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Jam Pulang</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Total Jam</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Status Presensi</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Lokasi</TableHead></TableRow></TableHeader>
                <TableBody>
                  {history.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-10 text-[#64748B]">Tidak ada data periode ini.</TableCell></TableRow> : history.map((h) => (
                    <TableRow key={h.id} data-testid={`attendance-row-${h.date}`} className="hover:bg-[#eff4ff]/60">
                      <TableCell className="font-medium text-[#0b1c30]">{h.date}</TableCell>
                      <TableCell className="tabular-nums font-semibold text-[#00364e]">{h.check_in?.time || "—"}</TableCell>
                      <TableCell className="tabular-nums">{h.check_out?.time || "—"}</TableCell>
                      <TableCell className="tabular-nums">{formatDuration(h.duration_minutes)}</TableCell>
                      <TableCell><AttendanceStatusPill status={h.status} /></TableCell>
                      <TableCell className="text-[14px] max-w-[220px]">
                        {h.check_in?.lat ? (
                          <div>
                            <div className="tabular-nums">{h.check_in.lat.toFixed(4)}, {h.check_in.lng.toFixed(4)}</div>
                            {h.check_in.address && <div className="text-[#64748B] truncate" title={h.check_in.address}>{h.check_in.address}</div>}
                          </div>
                        ) : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
        </>)}

        {tab === "sakitcuti" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-[20px] font-bold text-[#0b1c30]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Pengajuan Sakit &amp; Cuti</h2>
            <div><Button onClick={()=>setLeaveOpen(true)} className="gap-2 bg-[#00364e] hover:bg-[#1b4d68] text-white" data-testid="open-leave-dialog"><span className="material-symbols-outlined text-[18px]">send</span> Ajukan Sakit/Cuti</Button></div>
          </div>
            <Card className="overflow-hidden overflow-x-auto bg-white border-[#E2E8F0]/80 shadow-sm">
              <Table>
                <TableHeader><TableRow className="bg-[#eff4ff]"><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Tipe</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Rentang</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Alasan</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Status</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Catatan Admin</TableHead></TableRow></TableHeader>
                <TableBody>
                  {filteredSakitCuti.length===0 ? <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Belum ada pengajuan Sakit/Cuti pada periode ini.</TableCell></TableRow> : filteredSakitCuti.map((l)=>(
                    <TableRow key={l.id}><TableCell className="capitalize">{typeLabel(l.type)}</TableCell><TableCell>{l.start_date} → {l.end_date}</TableCell><TableCell className="max-w-xs truncate">{l.reason||"—"}</TableCell><TableCell><Badge variant="outline" className={l.status==="approved"?"border-[hsl(var(--success))] text-[hsl(var(--success))]": l.status==="rejected"?"border-destructive text-destructive":"border-[hsl(var(--warning))] text-[hsl(var(--warning))]"}>{l.status}</Badge></TableCell><TableCell>{l.admin_note||"—"}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <LeaveDialog open={leaveOpen} setOpen={setLeaveOpen} onDone={()=>{loadLeaves(); loadHistory();}} title="Ajukan Sakit / Cuti" types={[{value:"sakit",label:"Sakit"},{value:"cuti",label:"Cuti"}]} defaultType="sakit" testIdPrefix="leave" />
        </div>
        )}

        {tab === "terlambat" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-[20px] font-bold text-[#0b1c30]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Terlambat &amp; Pulang Cepat</h2>
            <div><Button onClick={()=>setLateOpen(true)} className="gap-2 bg-[#00364e] hover:bg-[#1b4d68] text-white" data-testid="open-late-dialog"><span className="material-symbols-outlined text-[18px]">alarm_off</span> Ajukan Terlambat/Pulang Cepat</Button></div>
          </div>
            <Card className="overflow-hidden overflow-x-auto bg-white border-[#E2E8F0]/80 shadow-sm">
              <Table>
                <TableHeader><TableRow className="bg-[#eff4ff]"><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Tipe</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Rentang</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Alasan</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Status</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Catatan Admin</TableHead></TableRow></TableHeader>
                <TableBody>
                  {filteredTerlambat.length===0 ? <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Belum ada pengajuan terlambat/pulang cepat pada periode ini.</TableCell></TableRow> : filteredTerlambat.map((l)=>(
                    <TableRow key={l.id}><TableCell className="capitalize">{typeLabel(l.type)}</TableCell><TableCell>{l.start_date} → {l.end_date}</TableCell><TableCell className="max-w-xs truncate">{l.reason||"—"}</TableCell><TableCell><Badge variant="outline" className={l.status==="approved"?"border-[hsl(var(--success))] text-[hsl(var(--success))]": l.status==="rejected"?"border-destructive text-destructive":"border-[hsl(var(--warning))] text-[hsl(var(--warning))]"}>{l.status}</Badge></TableCell><TableCell>{l.admin_note||"—"}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <LeaveDialog open={lateOpen} setOpen={setLateOpen} onDone={()=>{loadLeaves(); loadHistory();}} title="Ajukan Datang Terlambat / Pulang Lebih Cepat" types={[{value:"terlambat",label:"Datang Terlambat"},{value:"pulang_cepat",label:"Pulang Lebih Cepat"}]} defaultType="terlambat" testIdPrefix="late" />
        </div>
        )}

        {tab === "koreksi" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-[20px] font-bold text-[#0b1c30]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Koreksi Absensi</h2>
            <div><Button onClick={()=>setCorrOpen(true)} className="gap-2 bg-[#00364e] hover:bg-[#1b4d68] text-white" data-testid="open-correction-dialog"><span className="material-symbols-outlined text-[18px]">edit_note</span> Ajukan Koreksi</Button></div>
          </div>
            <Card className="overflow-hidden overflow-x-auto bg-white border-[#E2E8F0]/80 shadow-sm">
              <Table>
                <TableHeader><TableRow className="bg-[#eff4ff]"><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Tanggal</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Alasan</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Usulan</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Status</TableHead><TableHead className="text-[11px] uppercase tracking-wider text-[#64748B]">Catatan Admin</TableHead></TableRow></TableHeader>
                <TableBody>
                  {filteredCorrections.length===0 ? <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Belum ada koreksi pada periode ini.</TableCell></TableRow> : filteredCorrections.map((c)=>(
                    <TableRow key={c.id}><TableCell>{c.date}</TableCell><TableCell className="max-w-xs truncate">{c.reason}</TableCell><TableCell className="text-xs">{[c.proposed_check_in && `In: ${c.proposed_check_in}`, c.proposed_check_out && `Out: ${c.proposed_check_out}`, c.proposed_status && `Status: ${c.proposed_status}`].filter(Boolean).join(" · ") || "—"}</TableCell><TableCell><Badge variant="outline" className={c.status==="approved"?"border-[hsl(var(--success))] text-[hsl(var(--success))]": c.status==="rejected"?"border-destructive text-destructive":"border-[hsl(var(--warning))] text-[hsl(var(--warning))]"}>{c.status}</Badge></TableCell><TableCell>{c.admin_note||"—"}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <CorrectionDialog open={corrOpen} setOpen={setCorrOpen} onDone={()=>{loadCorr(); loadHistory();}} />
        </div>
        )}
      </div>
      <CaptureDialog open={!!captureMode} onOpenChange={(v)=>!v && setCaptureMode(null)} title={captureMode==="in" ? "Check-in Selfie" : "Check-out Selfie"} gps={gps} setGps={setGps} onConfirm={(file,pos)=> captureMode==="in" ? doCheckIn(file,pos) : doCheckOut(file,pos)} />
    </AppLayout>
  );
}

function LeaveDialog({ open, setOpen, onDone, title, types, defaultType, testIdPrefix }) {
  const [form, setForm] = useState({ type: defaultType, start_date: "", end_date: "", reason: "" });
  const [proof, setProof] = useState(null);
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const entryGuarded = useEntryGuard(open);
  const isDirty = form.start_date !== "" || form.end_date !== "" || form.reason.trim() !== "" || proof;
  const requestClose = (next) => {
    if (!next && isDirty) {
      setConfirmClose(true);
      return;
    }
    setOpen(next);
  };
  useEffect(() => { if (open) setForm((f)=> ({...f, type: defaultType})); }, [open, defaultType]);
  const toISO = (ddmmyyyy) => {
    if (!ddmmyyyy) return "";
    const [d,m,y] = ddmmyyyy.split("/").map(Number);
    if (!d||!m||!y) return "";
    return `${String(y).padStart(4,"0")}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  };
  const submit = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Lengkapi rentang tanggal"); return; }
    const startISO = toISO(form.start_date);
    const endISO = toISO(form.end_date);
    setSaving(true);
    try {
      let proof_path = null;
      if (proof) {
        const fd = new FormData(); fd.append("file", proof);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        proof_path = up.data.path;
      }
      await api.post("/attendance/leave-requests", { type: form.type, start_date: startISO, end_date: endISO, reason: form.reason, proof_path });
      toast.success("Pengajuan terkirim, menunggu persetujuan admin");
      setOpen(false); setForm({ type: defaultType, start_date: "", end_date: "", reason: "" }); setProof(null); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setSaving(false); }
  };
  return (
    <>
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent className={`max-w-md${entryGuarded ? " pointer-events-none" : ""}`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Tipe</Label>
            <Select value={form.type} onValueChange={(v)=>setForm({...form, type:v})}>
              <SelectTrigger data-testid={`${testIdPrefix}-type-select`}><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((t)=><SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3 leave-date-grid">
            <div className="space-y-1.5 min-w-0"><Label>Tanggal Mulai</Label><DatePicker value={form.start_date} onChange={(v)=>setForm({...form, start_date:v})} testId={`${testIdPrefix}-start-date`} /></div>
            <div className="space-y-1.5 min-w-0"><Label>Tanggal Selesai</Label><DatePicker value={form.end_date} onChange={(v)=>setForm({...form, end_date:v})} testId={`${testIdPrefix}-end-date`} /></div>
          </div>
          <div className="space-y-1.5"><Label>Alasan</Label><Textarea value={form.reason} onChange={(e)=>setForm({...form, reason:e.target.value})} placeholder="Keterangan opsional" data-testid={`${testIdPrefix}-reason`} /></div>
          <div className="space-y-1.5"><Label>Bukti (opsional)</Label>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e)=>setProof(e.target.files[0])} data-testid={`${testIdPrefix}-proof-input`} />
            <Button variant="outline" className="w-full justify-start" onClick={()=>fileRef.current?.click()}>{proof ? proof.name : "Upload bukti"}</Button>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving} data-testid={`submit-${testIdPrefix}`}>{saving?"Menyimpan…":"Kirim Pengajuan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
    <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Tutup form?</AlertDialogTitle>
          <AlertDialogDescription>
            Isian pengajuan yang belum dikirim akan hilang. Lanjut mengisi atau tutup form?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Lanjut Mengisi</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={() => { setConfirmClose(false); setOpen(false); }}
          >
            Ya, Tutup
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

function CorrectionDialog({ open, setOpen, onDone }) {
  const [form, setForm] = useState({ date: "", reason: "", proposed_check_in: "", proposed_check_out: "", proposed_status: "" });
  const [saving, setSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const entryGuarded = useEntryGuard(open);
  const isDirty = form.date !== "" || form.reason.trim() !== "" || form.proposed_check_in !== "" || form.proposed_check_out !== "" || form.proposed_status !== "";
  const requestClose = (next) => {
    if (!next && isDirty) {
      setConfirmClose(true);
      return;
    }
    setOpen(next);
  };
  const toISO = (ddmmyyyy) => {
    if (!ddmmyyyy) return "";
    const [d,m,y] = ddmmyyyy.split("/").map(Number);
    return `${String(y).padStart(4,"0")}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  };
  const submit = async () => {
    if (!form.date || !form.reason) { toast.error("Tanggal dan alasan wajib diisi"); return; }
    setSaving(true);
    try {
      await api.post("/attendance/corrections", {
        date: toISO(form.date),
        reason: form.reason,
        proposed_check_in: form.proposed_check_in || null,
        proposed_check_out: form.proposed_check_out || null,
        proposed_status: form.proposed_status || null,
      });
      toast.success("Koreksi diajukan");
      setOpen(false); setForm({ date: "", reason: "", proposed_check_in: "", proposed_check_out: "", proposed_status: "" }); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setSaving(false); }
  };
  return (
    <>
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent className={`max-w-md${entryGuarded ? " pointer-events-none" : ""}`}>
        <DialogHeader><DialogTitle>Ajukan Koreksi Absensi</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Tanggal Koreksi</Label><DatePicker value={form.date} onChange={(v)=>setForm({...form, date:v})} testId="correction-date" /></div>
          <div className="space-y-1.5"><Label>Alasan</Label><Textarea value={form.reason} onChange={(e)=>setForm({...form, reason:e.target.value})} placeholder="Contoh: Lupa check-out" data-testid="correction-reason" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Usulan Jam Masuk (HH:MM)</Label><Input placeholder="08:30" value={form.proposed_check_in} onChange={(e)=>setForm({...form, proposed_check_in:e.target.value})} data-testid="correction-in" /></div>
            <div className="space-y-1.5"><Label>Usulan Jam Pulang (HH:MM)</Label><Input placeholder="17:00" value={form.proposed_check_out} onChange={(e)=>setForm({...form, proposed_check_out:e.target.value})} data-testid="correction-out" /></div>
          </div>
          <div className="space-y-1.5"><Label>Usulan Status</Label>
            <Select value={form.proposed_status || "none"} onValueChange={(v)=>setForm({...form, proposed_status: v==="none"?"":v})}>
              <SelectTrigger data-testid="correction-status"><SelectValue placeholder="Tidak diubah" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Tidak diubah</SelectItem><SelectItem value="hadir">Hadir</SelectItem><SelectItem value="sakit">Sakit</SelectItem><SelectItem value="cuti">Cuti</SelectItem><SelectItem value="terlambat">Terlambat</SelectItem><SelectItem value="pulang_cepat">Pulang Cepat</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving} data-testid="submit-correction">{saving?"Menyimpan…":"Kirim Koreksi"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
    <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Tutup form?</AlertDialogTitle>
          <AlertDialogDescription>
            Isian koreksi yang belum dikirim akan hilang. Lanjut mengisi atau tutup form?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Lanjut Mengisi</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={() => { setConfirmClose(false); setOpen(false); }}
          >
            Ya, Tutup
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
